import { useEffect, useRef, useState } from "react";
import { inputCls } from "./ui";

export type MultiSelectOption = { value: string; label: string; locked?: boolean; hint?: string };

const Check = ({ on }: { on: boolean }) => (
  <svg viewBox="0 0 20 20" className={`size-4 shrink-0 ${on ? "" : "invisible"}`} fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.8 3.79 6.8-6.8a1 1 0 0 1 1.4 0Z" clipRule="evenodd" />
  </svg>
);

/**
 * Dropdown to pick several options; a picked one shows a check mark. Locked options are always selected and cannot be
 * toggled. A long list scrolls inside the dropdown; `search` adds a box at its top that narrows it as you type (people).
 */
export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "None",
  summary,
  wide = false,
  all,
  search,
}: {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  summary?: (chosen: MultiSelectOption[]) => string;
  /** Fill the width and look like the form's other inputs. */
  wide?: boolean;
  /** A first row that picks every option at once, or takes them all off again, e.g. "All your agents". */
  all?: string;
  /** The search box's placeholder, e.g. "Search people": shown when given. */
  search?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    // Escape closes the dropdown alone: the popup it sits in listens on window, after this, and stays open.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const chosen = options.filter((o) => o.locked || selected.includes(o.value));
  const free = options.filter((o) => !o.locked);
  const every = free.length > 0 && free.every((o) => selected.includes(o.value));
  const label = summary ? summary(chosen) : chosen.length ? chosen.map((o) => o.label).join(", ") : placeholder;
  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  return (
    <div ref={root} className={`relative w-full ${wide ? "" : "inline-block max-w-xs"}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={
          wide
            ? `${inputCls} flex items-center gap-2 text-left`
            : "flex w-full items-center gap-2 rounded-md border border-line bg-surface px-3 py-1.5 text-left text-sm hover:border-faint"
        }
      >
        <span className={`min-w-0 flex-1 truncate ${chosen.length ? "" : "text-faint"}`}>{label}</span>
        <svg viewBox="0 0 20 20" className={`size-4 shrink-0 text-faint transition-transform ${open ? "rotate-180" : ""}`} fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.2 7.2a.75.75 0 0 1 1.06 0L10 10.94l3.74-3.74a.75.75 0 1 1 1.06 1.06l-4.27 4.27a.75.75 0 0 1-1.06 0L5.2 8.26a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div role="listbox" aria-multiselectable="true" className="absolute left-0 z-30 mt-1 max-h-72 w-full min-w-56 overflow-y-auto card p-1 shadow-lg">
          {search && (
            <input className={`${inputCls} mb-1`} autoFocus value={query} placeholder={search} aria-label={search} onChange={(e) => setQuery(e.target.value)} />
          )}
          {all && free.length > 1 && (
            <button
              type="button"
              role="option"
              aria-selected={every}
              onClick={() => onChange(every ? selected.filter((v) => !free.some((o) => o.value === v)) : [...new Set([...selected, ...free.map((o) => o.value)])])}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm font-medium hover:bg-fill-subtle"
            >
              <span className="flex-1">{all}</span>
              <Check on={every} />
            </button>
          )}
          {all && free.length > 1 && <div className="mx-2 my-1 h-px bg-fill" />}
          {shown.map((o) => {
            const checked = o.locked || selected.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={checked}
                disabled={o.locked}
                title={o.hint}
                onClick={() => onChange(checked ? selected.filter((v) => v !== o.value) : [...new Set([...selected, o.value])])}
                className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm ${o.locked ? "cursor-default text-faint" : "hover:bg-fill-subtle"}`}
              >
                <span className="flex-1">{o.label}</span>
                {o.hint && <span className="text-tiny text-faint">{o.hint}</span>}
                <Check on={checked} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
